import { Icon, Button } from '@polichat/flamboyant';
import styles from './NewCreditsCard.module.css';
import Tippy from '@tippyjs/react';
import { ChannelUsage, ExtraUsage, GroupUsage } from '../CreditsCard';
import useGlotaT from 'src/hooks/useGlotaTranslation';

interface MappedExtraUsage extends ExtraUsage {
  translatedLabel: string;
}

interface NewCardProps {
  billingAi: boolean;
  channelTitle: string;
  channels: ChannelUsage[];
  onBuy?: () => void;
  newBillingModel: boolean;
  detachedCreditsTotal: number;
  groups?: Record<string, GroupUsage>;
  availableDisplayed: number;
  totalConsumed: number;
  percentDisplay: number;
  mappedExtras: MappedExtraUsage[];
}

export const NewCard = ({
  billingAi,
  channelTitle,
  channels,
  onBuy,
  newBillingModel,
  detachedCreditsTotal,
  groups,
  availableDisplayed,
  totalConsumed,
  percentDisplay,
  mappedExtras,
}: NewCardProps) => {
  const creditsConsumptionText = useGlotaT('spa.tools.creditsConsumption');
  const availableCreditsText = useGlotaT('spa.tools.availableCredits');
  const consumedThisCycleText = useGlotaT('spa.tools.consumedThisCycle');
  const consumptionThisCycleText = useGlotaT('spa.tools.consumptionThisCycle');
  const consumedText = useGlotaT('spa.tools.consumed');
  const buyCreditsText = useGlotaT('spa.tools.buyCredits');
  const wabaAccountText = useGlotaT('spa.tools.wabaAccount');

  const formatNumber = (n: number) =>
    n?.toLocaleString('pt-BR', { minimumFractionDigits: 0 });

  return (
    <div className={styles.card}>
      <h3 className={styles.creditsBoxTitle}>{creditsConsumptionText}</h3>

      <div className={styles.creditsBox}>
        <div className={styles.header}>
          <span className={styles.label}>{availableCreditsText}</span>
          <Tippy
            theme="dark"
            placement="right"
            trigger="mouseenter"
            hideOnClick={false}
            appendTo={() => document.body}
            interactive
            arrow={true}
            content={
              <div style={{ maxWidth: '212px', whiteSpace: 'normal' }}>
                {formatNumber(availableDisplayed || 0)}
              </div>
            }
          >
            <span className={`${styles.value} ${styles.availableCredits}`}>
              {formatNumber(availableDisplayed || 0)}
            </span>
          </Tippy>
        </div>

        <hr className={styles.divider} />

        <div className={styles.body}>
          <div className={styles.consumedLine}>
            <span>{consumedThisCycleText}</span>
            <span>{`${formatNumber(totalConsumed ?? 0)} / ${formatNumber(
              detachedCreditsTotal ?? 0
            )}`}</span>
          </div>

          <div className={styles.progressBar}>
            <div
              className={styles.progress}
              style={{ width: `${percentDisplay}%` }}
            />
          </div>

          <div className={styles.percentText}>
            {`${percentDisplay.toFixed(1)}% ${consumedText}`}
          </div>
        </div>
      </div>

      <Button color="primary" className={styles.buyButton} onClick={onBuy}>
        {buyCreditsText}
      </Button>

      <div className={styles.channelsSection}>
        <h4 className={styles.channelsTitle}>{consumptionThisCycleText}</h4>

        <div className={styles.channelsContent}>
          <div className={styles.channelsHeader}>
            <span className={styles.channelsHeaderLabel}>{channelTitle}</span>
            <span className={styles.channelsHeaderValue}>
              {channels.length}
            </span>
          </div>

          <ul className={styles.channelList}>
            {newBillingModel ? (
              <>
                {channels.map((ch) => (
                  <li key={ch.label} className={styles.channelItem}>
                    {typeof ch.icon === 'string' ? (
                      <span className={styles.channelIcon}>
                        <Icon icon={ch.icon} className={styles.channelIcon} />
                      </span>
                    ) : (
                      <span className={styles.channelIcon}>{ch.icon}</span>
                    )}
                    <span className={styles.channelLabel}>{ch.label}</span>
                    <span className={styles.channelValue}>
                      {formatNumber(ch.value ?? 0)}
                    </span>
                  </li>
                ))}
              </>
            ) : (
              <>
                {groups && Object.values(groups).length === 0 && (
                  <li className={styles.channelItem}>
                    <span className={styles.channelIcon}>
                      <Icon
                        icon="poli-icon pi-whatsapp-api-fill"
                        className={styles.channelIcon}
                      />
                    </span>
                    <span className={styles.channelLabel}>
                      {wabaAccountText}
                    </span>
                    <span className={styles.channelValue}>0/0</span>
                  </li>
                )}

                {groups &&
                  Object.values(groups).map((group) => {
                    const displayUsage = `${formatNumber(
                      group.groupUsed
                    )}/${formatNumber(group.groupTotal ?? 0)}`;

                    return (
                      <li
                        key={group.groupId}
                        className={styles.channelItem}
                        style={{
                          flexDirection: 'column',
                          alignItems: 'stretch',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                          }}
                        >
                          <span className={styles.channelIcon}>
                            <Icon
                              icon="poli-icon pi-whatsapp-api-fill"
                              className={styles.channelIcon}
                            />
                          </span>
                          <span
                            className={styles.channelLabel}
                            style={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {group.wabaName}
                          </span>
                          <span
                            style={{ marginLeft: 'auto' }}
                            className={styles.channelValue}
                          >
                            {displayUsage}
                          </span>
                        </div>

                        <ul
                          className={`${styles.channelList} ${
                            styles.channelSubList ? styles.channelSubList : ''
                          }`}
                        >
                          {group.channels.map((ch) => (
                            <li key={ch.label} className={styles.channelItem}>
                              <span className={styles.channelIcon}></span>
                              <span
                                className={styles.channelLabel}
                                style={{ fontSize: '13px' }}
                              >
                                {ch.label}
                              </span>
                              <span className={styles.channelValue}>
                                {formatNumber(ch.value ?? 0)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </li>
                    );
                  })}
              </>
            )}
          </ul>
        </div>
      </div>

      <div className={styles.extraList}>
        {mappedExtras.map((item, index) => {
          const IconExtra =
            typeof item.icon === 'string' ? (
              <Icon icon={item.icon} className={styles.extraIcon} />
            ) : (
              <span className={styles.extraIcon}>{item.icon}</span>
            );
          const mappedExtrasLength = !billingAi
            ? mappedExtras.length - 1
            : mappedExtras.length;
          const widthLastItem =
            mappedExtrasLength % 2 !== 0 && index === mappedExtras.length - 1
              ? '100%'
              : '48%';
          if (item.id === 'ai' && !billingAi) {
            return <></>;
          }
          return (
            <div
              key={item.translatedLabel}
              className={styles.extraItem}
              style={{
                width: widthLastItem,
              }}
            >
              <div className={styles.extraLeft}>
                {item.info ? (
                  <Tippy
                    theme="dark"
                    placement="right"
                    trigger="mouseenter"
                    hideOnClick={false}
                    appendTo={() => document.body}
                    interactive
                    arrow={true}
                    content={
                      <div style={{ maxWidth: '212px', whiteSpace: 'normal' }}>
                        {item.info}
                      </div>
                    }
                  >
                    {IconExtra}
                  </Tippy>
                ) : (
                  IconExtra
                )}

                <div className={styles.extraLabelContainer}>
                  <span className={styles.extraLabel}>
                    {item.translatedLabel}
                  </span>
                  {typeof item.value === 'number' && (
                    <span className={styles.extraValue}>
                      {formatNumber(item.value ?? 0)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
